import { useMemo } from 'react';
import { Card, Col, Form, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Badge from '../base/Badge';
import dayjs from 'dayjs';
import DatePicker from '../base/DatePicker.js';
import { MACHINES as MACHINE_NAMES } from '../../data/operatorConstants.js';

// What you are looking at, and what it is filtered by.
//
// Two things this replaces. The old bar showed five controls on every tab while
// two of the five endpoints behind them quietly ignored half — so on Overview
// and Downtime you changed a filter and nothing moved. Every filter now reaches
// every endpoint except where it is meaningless, and those are DISABLED with the
// reason on hover rather than left to look broken.
//
// And it had no default range at all, which meant each tab fell back to whatever
// window its own endpoint happened to pick — 30 days on one, 90 on another, so
// two panels on one screen answered about different months. It opens on the
// current month now, which also keeps the scan bounded as the history grows.

const MACHINES = MACHINE_NAMES.map(m => m.toLowerCase());

export const PHASES = [
  ['conductor_isolation', 'Isolation'],
  ['pre_stranding', 'Pre-stranding'],
  ['rope', 'Rope'],
  ['interior_sheat', 'Inner Sheath'],
  ['armouring', 'Armouring'],
  ['exterior_sheat', 'Outer Sheath'],
  ['coiling', 'Coiling'],
  ['rewinding', 'Rewinding'],
  ['unwinding', 'Unwinding']
];

/**
 * The opening range: this month to date.
 *
 * Exported because the page needs the same value to seed its state — a default
 * computed in two places drifts the first time one of them is edited.
 */
export const thisMonth = () => [
  dayjs().startOf('month').toDate(),
  dayjs().endOf('day').toDate()
];

export const RANGE_PRESETS = [
  { key: 'month', label: 'This month', of: () => thisMonth() },
  {
    key: 'last30',
    label: 'Last 30 days',
    of: () => [dayjs().subtract(29, 'day').startOf('day').toDate(), dayjs().endOf('day').toDate()]
  },
  {
    key: 'week',
    label: 'This week',
    of: () => [dayjs().startOf('week').toDate(), dayjs().endOf('day').toDate()]
  },
  {
    key: 'prevMonth',
    label: 'Last month',
    of: () => [
      dayjs().subtract(1, 'month').startOf('month').toDate(),
      dayjs().subtract(1, 'month').endOf('month').toDate()
    ]
  }
];

const sameDay = (a, b) => a && b && dayjs(a).isSame(dayjs(b), 'day');

/** Which preset a range corresponds to, so the buttons can show the state. */
const presetOf = (range) => {
  if (range?.length !== 2) return null;
  for (const p of RANGE_PRESETS) {
    const [from, to] = p.of();
    if (sameDay(range[0], from) && sameDay(range[1], to)) return p.key;
  }
  return null;
};

/**
 * Which filters each tab's endpoints actually honour.
 *
 * Declared rather than discovered: the alternative is what was here before, a
 * bar that offers everything everywhere and leaves you to find out which of them
 * moved the numbers. A `false` entry disables the control and says why.
 */
export const TAB_FILTERS = {
  overview: { machine: true, phase: true, product: true, operators: true },
  comparison: { machine: true, phase: true, product: true, operators: true },
  downtime: {
    machine: true,
    product: true,
    operators: true,
    phase: 'A stop is the gap BETWEEN two drums, which can belong to different phases'
  },
  products: { machine: true, phase: true, product: true, operators: true },
  material: { machine: true, phase: true, product: true, operators: true }
};

const Disabled = ({ why, children }) =>
  why && typeof why === 'string' ? (
    <OverlayTrigger placement="top" overlay={<Tooltip>{why}</Tooltip>}>
      {/* a disabled control swallows its own mouse events, so the span carries them */}
      <span className="d-block">{children}</span>
    </OverlayTrigger>
  ) : (
    children
  );

const OperatorScopeBar = ({
  tab,
  machineId, setMachineId,
  phase, setPhase,
  productLabel, setProductLabel,
  operators, setOperators,
  dateRange, setDateRange,
  productOptions = [],
  scope = null
}) => {
  const applies = TAB_FILTERS[tab] || TAB_FILTERS.overview;
  const active = presetOf(dateRange);

  const chips = useMemo(() => {
    const out = [];
    if (machineId) out.push({ label: machineId.toUpperCase(), clear: () => setMachineId('') });
    if (phase) {
      const found = PHASES.find(([v]) => v === phase);
      out.push({ label: found ? found[1] : phase, clear: () => setPhase('') });
    }
    if (productLabel) out.push({ label: productLabel, clear: () => setProductLabel('') });
    for (const name of operators) {
      out.push({ label: name, clear: () => setOperators(operators.filter(o => o !== name)) });
    }
    return out;
  }, [machineId, phase, productLabel, operators, setMachineId, setPhase, setProductLabel, setOperators]);

  // Only the filters this tab ignores AND that are actually set are worth a
  // note — warning about a control nobody touched is noise.
  const ignored = Object.entries(applies)
    .filter(([key, v]) => typeof v === 'string')
    .filter(([key]) =>
      (key === 'phase' && phase) ||
      (key === 'product' && productLabel) ||
      (key === 'operators' && operators.length) ||
      (key === 'machine' && machineId)
    );

  // `text-400` is #3e465b on the dark theme — all but invisible on a #0f111a
  // page. A disabled control is already dimmed by the browser; the label only
  // has to stop competing, not disappear.
  const label = (text, ok) => (
    <Form.Label className={`mb-1 fs-9 text-700 ${ok === true ? '' : 'opacity-75'}`}>
      {text}
      {ok !== true && <span className="ms-1">·</span>}
    </Form.Label>
  );

  return (
    <Card className="mb-3">
      <Card.Body className="py-3">
        <Row className="g-3 align-items-end">
          <Col xs={12} lg={5}>
            <Form.Label className="mb-1 fs-9 text-700">Period</Form.Label>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <div className="btn-group btn-group-sm" role="group">
                {RANGE_PRESETS.map(p => (
                  <button
                    key={p.key}
                    type="button"
                    className={`btn ${active === p.key ? 'btn-primary' : 'btn-phoenix-secondary'}`}
                    onClick={() => setDateRange(p.of())}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div style={{ minWidth: 190 }}>
                <DatePicker
                  options={{ mode: 'range', dateFormat: 'M j, Y' }}
                  value={dateRange}
                  onChange={setDateRange}
                  placeholder="Custom range…"
                />
              </div>
            </div>
          </Col>

          <Col xs={6} md={4} lg={2}>
            <Disabled why={applies.machine}>
              {label('Machine', applies.machine)}
              <Form.Select
                size="sm"
                value={machineId}
                disabled={applies.machine !== true}
                onChange={e => setMachineId(e.target.value)}
              >
                <option value="">All machines</option>
                {MACHINES.map(m => (
                  <option key={m} value={m}>{m.toUpperCase()}</option>
                ))}
              </Form.Select>
            </Disabled>
          </Col>

          <Col xs={6} md={4} lg={2}>
            <Disabled why={applies.phase}>
              {label('Phase', applies.phase)}
              <Form.Select
                size="sm"
                value={phase}
                disabled={applies.phase !== true}
                onChange={e => setPhase(e.target.value)}
              >
                <option value="">All phases</option>
                {PHASES.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </Form.Select>
            </Disabled>
          </Col>

          <Col xs={12} md={4} lg={3}>
            <Disabled why={applies.product}>
              {label('Product', applies.product)}
              <Form.Select
                size="sm"
                value={productLabel}
                disabled={applies.product !== true}
                onChange={e => setProductLabel(e.target.value)}
              >
                <option value="">All products</option>
                {productOptions.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </Form.Select>
            </Disabled>
          </Col>
        </Row>

        {(scope || chips.length || ignored.length) && (
          <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-3 border-top">
            {scope && <span className="fs-9 text-700">{scope}</span>}

            {chips.map((c, i) => (
              <Badge
                key={`${c.label}-${i}`}
                bg="primary"
                variant="phoenix"
                className="fw-normal d-inline-flex align-items-center gap-1"
                style={{ cursor: 'pointer' }}
                onClick={c.clear}
                title="Remove"
              >
                {c.label}
                <span aria-hidden="true">×</span>
              </Badge>
            ))}

            {chips.length > 1 && (
              <button
                className="btn btn-sm btn-link text-decoration-none p-0 fs-9"
                onClick={() => {
                  setMachineId('');
                  setPhase('');
                  setProductLabel('');
                  setOperators([]);
                }}
              >
                Clear all
              </button>
            )}

            {ignored.map(([key, why]) => (
              <span key={key} className="fs-9 text-warning ms-auto">
                {key[0].toUpperCase() + key.slice(1)} is not applied on this tab — {why.toLowerCase()}
              </span>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default OperatorScopeBar;
